import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Spinner,
  Image,
  Progress,
  ProgressLabel,
} from '@chakra-ui/react';
import EventCard from '../EventCard';
import { getAvailableSeats } from '../../utils';

const EventsSlide = ({ backgroundImage, title }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch('/api/eventi/home');
        const data = await response.json();

        // L'API /api/eventi/home già restituisce solo eventi futuri, quindi non serve filtrare di nuovo
        const futureEvents = (data.events || [])
          .sort((a, b) => {
            const dateA = new Date(a.Date_end || a.Data);
            const dateB = new Date(b.Date_end || b.Data);
            return dateA - dateB;
          })
          .slice(0, 3);

        setEvents(futureEvents);
      } catch (error) {
        console.error('Errore nel caricamento eventi:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  if (loading) {
    return (
      <Box
        height="100vh"
        width="100vw"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="black"
      >
        <Spinner size="xl" color="white" />
      </Box>
    );
  }

  return (
    <Box
      height="100vh"
      width="100vw"
      bg="black"
      position="relative"
      overflow="hidden"
    >
      {/* Immagine di sfondo ruotata */}
      {backgroundImage && (
        <Image
          src={
            typeof backgroundImage === 'string'
              ? backgroundImage
              : backgroundImage.url || backgroundImage
          }
          alt="Background"
          position="absolute"
          top="50%"
          left="50%"
          width="100vh"
          height="100vw"
          objectFit="cover"
          opacity={0.7}
          zIndex={0}
          transform="translate(-50%, -50%) rotate(-90deg)"
          transformOrigin="center"
          onError={(e) => {
            console.error('Errore caricamento sfondo EventsSlide:', e);
            console.log('backgroundImage:', backgroundImage);
          }}
        />
      )}

      {/* Overlay scuro per leggibilità */}
      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        bg="blackAlpha.300"
        zIndex={1}
      />

      {/* Contenuto ruotato */}
      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={8}
        transform="rotate(-90deg)"
        transformOrigin="center"
        zIndex={2}
      >
        {/* Container con proporzioni 9:16 per schermo verticale */}
        <Box
          width="90vh"
          height="calc(90vh * 16 / 9)"
          maxWidth="90vw"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="flex-start"
          pt={8}
        >
          <Box mb={6} textAlign="center" zIndex={3}>
            <Heading
              fontSize="5xl"
              color="white"
              fontWeight="900"
              textShadow="2px 2px 4px rgba(0,0,0,0.8)"
              bg="blackAlpha.600"
              px={6}
              py={3}
              borderRadius={8}
            >
              {title || 'Prossimi Eventi'}
            </Heading>
          </Box>
          {events.length === 0 ? (
            <Text color="white" fontSize="xl">
              Nessun evento disponibile
            </Text>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              spacing={6}
              width="100%"
              px={8}
              gap={6}
              overflowY="auto"
            >
              {events.map((event) => {
                const eventCapacity =
                  event?.capienza || event?.Capienza || event?.Max_Participants;
                const availableSeats = getAvailableSeats(event) ?? 0;
                const progressValue = eventCapacity
                  ? (availableSeats / eventCapacity) * 100
                  : 0;

                // Colore del progress basato sulla disponibilità
                const getProgressColor = (value) => {
                  if (value < 25) return 'red';
                  if (value < 50) return 'yellow';
                  return 'green';
                };

                return (
                  <Box
                    key={event.id || event.documentId}
                    mb={4}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    width="100%"
                  >
                    <Box width="100%" display="flex" justifyContent="center">
                      <Box width="100%" maxWidth="550px">
                        <EventCard event={event} hideButton />
                      </Box>
                    </Box>
                    {/* Progress bar per posti disponibili */}
                    {eventCapacity && (
                      <Box
                        mt={3}
                        width="100%"
                        display="flex"
                        justifyContent="center"
                      >
                        <Box
                          width="100%"
                          maxWidth="550px"
                          boxShadow="lg"
                          borderRadius="md"
                        >
                          <Progress
                            hasStripe
                            height="36px"
                            colorScheme={getProgressColor(progressValue)}
                            value={progressValue}
                            borderRadius="md"
                          >
                            <ProgressLabel fontSize="11px" fontWeight="bold">
                              {availableSeats}/{eventCapacity}
                            </ProgressLabel>
                          </Progress>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EventsSlide;
